import React, { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import VideocamIcon from '@mui/icons-material/Videocam';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { getVideoStatus, getVideoUploadUrl, setVideoUrl } from '../services/internApi';

// Formats the backend accepts (see intern_operations.get_video_upload_url).
// Map extensions too because some browsers report an empty MIME type.
const TYPE_BY_EXT = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};
const MAX_SIZE_MB = 500;

/**
 * Intro-video upload card for the intern onboarding flow
 * (pending_video -> pending_review). Renders nothing once the intern is
 * past the video stage with no rejection outstanding.
 */
export default function VideoUploadCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [justUploaded, setJustUploaded] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    getVideoStatus()
      .then((data) => setStatus(data))
      .catch(() => setStatus(null)) // non-fatal — card simply hides
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setError('');

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const contentType = file.type || TYPE_BY_EXT[ext] || '';
    if (!Object.values(TYPE_BY_EXT).includes(contentType)) {
      setError(
        'Unsupported video format. Please upload an MP4, MOV, or WebM file — '
        + 'phone cameras record MP4 by default.'
      );
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`That file is over ${MAX_SIZE_MB} MB. A 3-minute phone video should be well under that — try re-exporting at a lower resolution.`);
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const { upload_url: uploadUrl, key } = await getVideoUploadUrl(contentType);

      // XMLHttpRequest instead of fetch: we want upload progress events.
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`Upload failed (HTTP ${xhr.status})`)));
        xhr.onerror = () => reject(new Error('Upload failed — check your connection and try again.'));
        xhr.send(file);
      });

      await setVideoUrl(key);
      setJustUploaded(true);
      load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return null;
  if (!status) return null;

  const { can_upload: canUpload, video_status: videoStatus, video_rejection_reason: rejectionReason } = status;

  // Past the video stage entirely (approved / later stages, nothing rejected).
  if (!canUpload && videoStatus !== 'rejected') return null;

  const awaitingReview = videoStatus === 'pending_review' || justUploaded;
  const rejected = videoStatus === 'rejected' && !justUploaded;

  return (
    <Paper sx={{ p: 3, mb: 3, border: '2px solid', borderColor: awaitingReview ? 'success.light' : 'primary.light' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <VideocamIcon color={awaitingReview ? 'success' : 'primary'} sx={{ fontSize: 30 }} />
        <Typography variant="h6">Introduction Video</Typography>
      </Stack>

      {awaitingReview ? (
        <Alert severity="success" icon={<CheckCircleIcon />}>
          <AlertTitle>Video received</AlertTitle>
          Your introduction video is in — our team will review it and email you the outcome.
          Nothing else is needed from you right now. If you want to replace it before review,
          you can upload a new take below.
        </Alert>
      ) : rejected ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Your video needs another take</AlertTitle>
          {rejectionReason || 'Our review team asked for a new version.'} Upload a new video below.
        </Alert>
      ) : (
        <>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Last step of your application: record a short video (about 3 minutes) and upload it here.
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1 }}>
            Tell us:
            <ul style={{ marginTop: 4, marginBottom: 4 }}>
              <li>Who you are and where you study</li>
              <li>Why cybersecurity — what got you into it</li>
              <li>Your goals and general availability</li>
            </ul>
            A phone selfie video is perfect. MP4, MOV, or WebM, up to {MAX_SIZE_MB} MB.
          </Typography>
        </>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {uploading ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">
              Uploading... {progress}% — keep this page open until it finishes.
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      ) : (
        <Button
          variant={awaitingReview ? 'outlined' : 'contained'}
          size="large"
          startIcon={<UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          sx={{ mt: 2 }}
        >
          {awaitingReview ? 'Replace video' : rejected ? 'Upload new video' : 'Upload your video'}
        </Button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
        hidden
        onChange={handlePick}
      />
    </Paper>
  );
}
